/**
 * Recovery for stale lazy-loaded chunks.
 *
 * A frontend deploy replaces every hashed JS chunk. A tab that was opened
 * before the deploy still points at the old file names, so the first
 * lazily-loaded route (Annotators, Manajemen Pengguna, …) fails with
 * "Failed to fetch dynamically imported module" — the SPA fallback used to
 * answer the missing file with index.html, which the browser refuses to
 * execute as a module. The route then never renders and the page stays blank.
 *
 * Reloading the tab fetches the new index.html and the matching chunks. The
 * URL the user was trying to reach is remembered so the reload can continue
 * the navigation instead of dumping them back on the previous page. A cooldown
 * keeps a genuinely missing chunk from looping forever.
 */
const RELOAD_KEY = 'chunk-reload-at';
const TARGET_KEY = 'chunk-reload-target';
const RELOAD_COOLDOWN_MS = 10_000;

let reloading = false;

export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i
    .test(message);
}

/** Hard-reload the tab once, so it picks up the currently deployed build. */
export function reloadForNewBuild(targetUrl?: string): void {
  if (reloading) return;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    if (targetUrl) sessionStorage.setItem(TARGET_KEY, targetUrl);
  } catch {
    /* storage unavailable — fall through and reload once per page load */
  }
  reloading = true;
  window.location.reload();
}

/**
 * URL whose lazy route failed before the reload. Consumed once so a retry
 * cannot loop, and `null` when this was a normal page load.
 */
export function takeReloadTarget(): string | null {
  try {
    const target = sessionStorage.getItem(TARGET_KEY);
    if (target) sessionStorage.removeItem(TARGET_KEY);
    return target;
  } catch {
    return null;
  }
}
